import { useState, useEffect, useCallback } from "react";
import ProfessionalLayout from "@/components/layout/ProfessionalLayout";
import api from "@/api";
import { toast } from "sonner";
import {
  Calendar, CheckCircle, Clock, FileText, Upload, User, Plus,
  AlertTriangle, RefreshCw, ChevronDown, Search, X, MoreVertical,
  XCircle, ArrowRightCircle, Edit2, Paperclip
} from "lucide-react";
import AppointmentFormModal from "@/components/rdv/AppointmentFormModal";
import HdjImportModal from "@/components/secretary/HdjImportModal";
import HdjImportHistory from "@/components/secretary/HdjImportHistory";
import SecretaryTasksPanel from "@/components/secretary/SecretaryTasksPanel";

// Libellés FR des types de demandes (cohérents avec backend ACTION_LABELS)
const REQUEST_TYPE_LABEL = {
  consultation: "Consultation",
  imagerie: "Imagerie",
  analyses: "Bilan biologique",
  chimio_hdj: "Cure HDJ",
  radiotherapie: "Radiothérapie",
  rcp: "Inscription RCP",
  avis_specialise: "Avis spécialisé",
  hospitalisation: "Hospitalisation",
  pose_pac: "Pose PAC",
  soins_support: "Soins de support",
  demande_bilan: "Bilan biologique",
  demande_imagerie: "Imagerie",
  demande_pharmacie: "Ordonnance pharmacie",
};
import {
  RefuseRequestModal, DoneManualModal, CancelAppointmentModal, InProgressButton
} from "@/components/rdv/RequestActionModals";

export default function SecretaryMedicalDashboard() {
  const [pendingRequests, setPendingRequests] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("missions");
  const [showCreate, setShowCreate] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [importRefresh, setImportRefresh] = useState(0);
  const [prefilledRequest, setPrefilledRequest] = useState(null);
  const [refuseTarget, setRefuseTarget] = useState(null);
  const [doneManualTarget, setDoneManualTarget] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [reqRes, apptRes] = await Promise.all([
        api.get("/appointments/requests/pending"),
        api.get("/appointments"),
      ]);
      setPendingRequests(reqRes.data || []);
      setAppointments(apptRes.data || []);
    } catch {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const urgentCount = pendingRequests.filter(r => r.urgency === "urgent").length;

  return (
    <ProfessionalLayout title="Secrétariat Médical">
      <div className="space-y-4 lg:space-y-6">
        {/* Quick action: create appointment */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <p className="text-sm text-slate-500">Gérez les demandes, créez des RDV et importez le planning HDJ.</p>
          <div className="flex flex-col sm:flex-row gap-2">
            <button
              onClick={() => setShowImport(true)}
              data-testid="sec-import-hdj-btn"
              className="flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 text-slate-700 rounded-lg text-sm font-semibold hover:bg-slate-50 transition-colors min-h-[44px] justify-center"
            >
              <Upload className="w-4 h-4" /> Importer planning HDJ
            </button>
            <button
              onClick={() => { setPrefilledRequest(null); setShowCreate(true); }}
              data-testid="sec-new-appointment-btn"
              className="flex items-center gap-2 px-4 py-2.5 bg-emerald-600 text-white rounded-lg text-sm font-semibold hover:bg-emerald-700 transition-colors min-h-[44px] justify-center"
            >
              <Plus className="w-4 h-4" /> Nouveau RDV
            </button>
          </div>
        </div>
        {showImport && <HdjImportModal onClose={() => setShowImport(false)} onDone={() => { fetchData(); setImportRefresh((k) => k + 1); }} />}

        {/* Summary */}
        <div className="grid grid-cols-3 gap-3 lg:gap-4" data-testid="secretary-med-summary">
          <div className="dash-card text-center">
            <p className="text-3xl font-bold text-orange-600">{pendingRequests.length}</p>
            <p className="text-sm text-slate-500">Demandes en attente</p>
          </div>
          <div className="dash-card text-center">
            <p className="text-3xl font-bold text-red-600">{urgentCount}</p>
            <p className="text-sm text-slate-500">Urgentes</p>
          </div>
          <div className="dash-card text-center">
            <p className="text-3xl font-bold text-emerald-600">{appointments.filter(a => a.status === "CONFIRMED").length}</p>
            <p className="text-sm text-slate-500">RDV confirmes</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-white rounded-xl border border-slate-200 p-1" data-testid="secretary-tabs">
          {[
            { key: "missions", label: "Missions", count: null },
            { key: "requests", label: "Demandes de RDV", count: pendingRequests.length },
            { key: "appointments", label: "RDV programmes", count: appointments.length },
            { key: "imports", label: "Imports planning", count: null },
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              data-testid={`tab-${tab.key}`}
              className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-semibold transition-all ${
                activeTab === tab.key
                  ? "bg-emerald-600 text-white shadow-sm"
                  : "text-slate-500 hover:text-slate-700 hover:bg-slate-50"
              }`}
            >
              {tab.label}
              {tab.count > 0 && (
                <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-xs font-bold ${
                  activeTab === tab.key ? "bg-white/20 text-white" : "bg-red-100 text-red-600"
                }`}>{tab.count}</span>
              )}
            </button>
          ))}
        </div>

        {/* Pending Requests */}
        {activeTab === "requests" && (
          <div className="space-y-3" data-testid="pending-requests-list">
            {loading ? (
              <div className="space-y-3">{[...Array(3)].map((_, i) => <div key={i} className="h-20 bg-slate-100 rounded-xl animate-pulse" />)}</div>
            ) : pendingRequests.length === 0 ? (
              <div className="dash-card text-center py-10">
                <CheckCircle className="w-12 h-12 text-emerald-300 mx-auto mb-3" />
                <p className="font-semibold text-slate-600">Aucune demande en attente</p>
              </div>
            ) : (
              pendingRequests.map(req => (
                <div
                  key={req.id}
                  data-testid={`request-card-${req.id}`}
                  className={`dash-card border-l-4 ${req.urgency === "urgent" ? "border-red-500" : "border-cyan-500"}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${req.urgency === "urgent" ? "bg-red-100" : "bg-cyan-100"}`}>
                        {req.urgency === "urgent" ? <AlertTriangle className="w-5 h-5 text-red-600" /> : <Calendar className="w-5 h-5 text-cyan-600" />}
                      </div>
                      <div className="min-w-0">
                        <p className="font-semibold text-slate-800 truncate">{req.patientName}</p>
                        <div className="flex items-center gap-2 text-xs text-slate-500 flex-wrap">
                          <span className="capitalize">{(REQUEST_TYPE_LABEL[req.type] || (req.type || "consultation")).replace(/_/g, " ")}</span>
                          <span>·</span>
                          <span>Demandée par {req.requesterName}</span>
                          {req.patientPhone && <span>· {req.patientPhone}</span>}
                          {req.type?.startsWith("demande_") && (
                            <span className="ml-1 px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-[10px] font-semibold">SANS ORDONNANCE</span>
                          )}
                          {req.status === "IN_PROGRESS" && (
                            <span className="ml-1 px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded-full text-[10px] font-semibold">EN COURS</span>
                          )}
                        </div>
                        {req.notes && <p className="text-xs text-slate-400 mt-1">{req.notes}</p>}
                        {req.preferredDate && (
                          <p className="text-xs text-cyan-600 mt-1">
                            Date souhaitée : {new Date(req.preferredDate).toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short" })}
                          </p>
                        )}
                        {Array.isArray(req.attachedDocumentIds) && req.attachedDocumentIds.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`req-attachments-${req.id}`}>
                            {req.attachedDocumentIds.map((docId) => (
                              <a
                                key={docId}
                                href={`${window.location.hostname === "hana.ma" || window.location.hostname === "www.hana.ma" ? window.location.origin : process.env.REACT_APP_BACKEND_URL}/api/documents/${docId}/download`}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] bg-emerald-50 text-emerald-700 rounded border border-emerald-200 hover:bg-emerald-100 transition-colors"
                              >
                                <Paperclip className="w-3 h-3" />
                                Pièce jointe
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
                      <button
                        onClick={() => { setPrefilledRequest(req); setShowCreate(true); }}
                        data-testid={`plan-rdv-${req.id}`}
                        className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-xl text-sm font-medium hover:bg-emerald-700 transition-colors min-h-[44px] flex-shrink-0"
                      >
                        <Calendar className="w-4 h-4" />
                        <span className="hidden sm:inline">Planifier RDV</span>
                        <span className="sm:hidden">RDV</span>
                      </button>
                    <div className="relative">
                      <button
                        onClick={() => setOpenMenuId(openMenuId === req.id ? null : req.id)}
                        data-testid={`req-menu-${req.id}`}
                        className="p-2 rounded-xl text-slate-500 hover:bg-slate-100 min-h-[44px] min-w-[44px] flex items-center justify-center"
                        title="Autres actions"
                      >
                        <MoreVertical className="w-4 h-4" />
                      </button>
                      {openMenuId === req.id && (
                        <>
                          <div className="fixed inset-0 z-20" onClick={() => setOpenMenuId(null)} />
                          <div className="absolute right-0 top-full mt-1 w-56 bg-white border border-slate-200 rounded-xl shadow-lg z-30 py-1" data-testid={`req-menu-panel-${req.id}`}>
                            {req.status === "PENDING" && (
                              <div className="px-2 py-1">
                                <InProgressButton
                                  request={req}
                                  onDone={() => { setOpenMenuId(null); fetchData(); }}
                                  small
                                />
                              </div>
                            )}
                            <button
                              onClick={() => { setOpenMenuId(null); setDoneManualTarget(req); }}
                              data-testid={`req-action-done-manual-${req.id}`}
                              className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-amber-50 flex items-center gap-2"
                            >
                              <ArrowRightCircle className="w-4 h-4 text-amber-600" />
                              Marquer fait hors Hana
                            </button>
                            <button
                              onClick={() => { setOpenMenuId(null); setRefuseTarget(req); }}
                              data-testid={`req-action-refuse-${req.id}`}
                              className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-red-50 flex items-center gap-2"
                            >
                              <XCircle className="w-4 h-4 text-red-600" />
                              Refuser la demande
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Appointments list */}
        {activeTab === "appointments" && (
          <div className="space-y-3" data-testid="appointments-list">
            {appointments.length === 0 ? (
              <div className="dash-card text-center py-10">
                <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">Aucun RDV programme</p>
                <p className="text-xs text-slate-400 mt-1">Les RDV apparaitront ici une fois synchronises depuis le logiciel clinique</p>
              </div>
            ) : (
              appointments.map(appt => {
                const statusStyles = {
                  PROPOSED: { bg: "bg-blue-100", text: "text-blue-700", label: "En attente" },
                  CONFIRMED: { bg: "bg-emerald-100", text: "text-emerald-700", label: "Confirme" },
                  CANCELLED: { bg: "bg-red-100", text: "text-red-700", label: "Annule" },
                  COMPLETED: { bg: "bg-slate-100", text: "text-slate-600", label: "Termine" },
                };
                const st = statusStyles[appt.status] || statusStyles.PROPOSED;
                return (
                  <div key={appt.id} className="dash-card" data-testid={`appointment-${appt.id}`}>
                    <div className="flex items-center justify-between">
                      <div className="min-w-0">
                        <p className="font-semibold text-slate-800 flex items-center gap-2">
                          {appt.patientName || "Patient"}
                          {appt.source === "HANA_MANUAL" && (
                            <span
                              data-testid={`declared-badge-${appt.id}`}
                              title="RDV déclaratif, non synchronisé avec le logiciel clinique officiel."
                              className="text-[10px] font-semibold px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded-full"
                            >
                              Déclaratif
                            </span>
                          )}
                        </p>
                        <div className="flex items-center gap-2 text-xs text-slate-500 flex-wrap">
                          <span className="capitalize">{(appt.type || "consultation").replace("_", " ")}</span>
                          {appt.date && <span>· {new Date(appt.date).toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</span>}
                          {appt.doctorName && <span>· {appt.doctorName}</span>}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${st.bg} ${st.text}`}>{st.label}</span>
                        {appt.status !== "CANCELLED" && appt.status !== "COMPLETED" && (
                          <div className="relative">
                            <button
                              onClick={() => setOpenMenuId(openMenuId === `a-${appt.id}` ? null : `a-${appt.id}`)}
                              data-testid={`appt-menu-${appt.id}`}
                              className="p-2 rounded-xl text-slate-500 hover:bg-slate-100"
                              title="Actions"
                            >
                              <MoreVertical className="w-4 h-4" />
                            </button>
                            {openMenuId === `a-${appt.id}` && (
                              <>
                                <div className="fixed inset-0 z-20" onClick={() => setOpenMenuId(null)} />
                                <div className="absolute right-0 top-full mt-1 w-44 bg-white border border-slate-200 rounded-xl shadow-lg z-30 py-1">
                                  <button
                                    onClick={() => { setOpenMenuId(null); setEditTarget(appt); }}
                                    data-testid={`appt-action-edit-${appt.id}`}
                                    className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-emerald-50 flex items-center gap-2"
                                  >
                                    <Edit2 className="w-4 h-4 text-emerald-600" />
                                    Modifier
                                  </button>
                                  <button
                                    onClick={() => { setOpenMenuId(null); setCancelTarget(appt); }}
                                    data-testid={`appt-action-cancel-${appt.id}`}
                                    className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-red-50 flex items-center gap-2"
                                  >
                                    <XCircle className="w-4 h-4 text-red-600" />
                                    Annuler le RDV
                                  </button>
                                </div>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* Upload ordonnances */}
        {activeTab === "missions" && <SecretaryTasksPanel />}
        {activeTab === "imports" && <HdjImportHistory refreshKey={importRefresh} />}
      </div>

      {showCreate && (
        <AppointmentFormModal
          mode="create"
          prefilledRequest={prefilledRequest}
          onClose={() => { setShowCreate(false); setPrefilledRequest(null); }}
          onSubmitted={() => { setShowCreate(false); setPrefilledRequest(null); fetchData(); }}
          allowedTypes={["consultation", "analyses", "chimio_hdj", "radiotherapie"]}
        />
      )}
      {editTarget && (
        <AppointmentFormModal
          mode="edit"
          initialAppointment={editTarget}
          onClose={() => setEditTarget(null)}
          onSubmitted={() => { setEditTarget(null); fetchData(); }}
          allowedTypes={["consultation", "analyses", "chimio_hdj", "radiotherapie"]}
        />
      )}
      {refuseTarget && (
        <RefuseRequestModal
          request={refuseTarget}
          onClose={() => setRefuseTarget(null)}
          onDone={() => { setRefuseTarget(null); fetchData(); }}
        />
      )}
      {doneManualTarget && (
        <DoneManualModal
          request={doneManualTarget}
          onClose={() => setDoneManualTarget(null)}
          onDone={() => { setDoneManualTarget(null); fetchData(); }}
        />
      )}
      {cancelTarget && (
        <CancelAppointmentModal
          appointment={cancelTarget}
          onClose={() => setCancelTarget(null)}
          onDone={() => { setCancelTarget(null); fetchData(); }}
        />
      )}
    </ProfessionalLayout>
  );
}


